import { memo, useState, useCallback } from 'react'
import { Stack, Title, Text, Paper, Group, Button, Anchor } from 'components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { IconChevronLeft, IconSparkles } from '@tabler/icons-react'
import { ARC_CARD_RADIUS_PX, ARC_CARD_SHADOW } from 'components/ArcAuthPage'
import { ARC_AI_GRADIENT, ARC_AI_GRADIENT_HOVER } from 'themes/ArcTheme'
import { useProjectForm } from '../hooks/useProjectForm'
import { useAreas, useArcGrades } from '../components/AboutStep/hooks'
import { AILoadingModal } from '../components/AILoadingModal'
import {
  ProjectDescriptionSection,
  TopicSelectionSection,
  GradeLevelSection,
  ProjectPlanningSection
} from '../components/AboutStep/components'

interface AIFlowViewProps {
  onBack: () => void
}

export const AIFlowView = memo<AIFlowViewProps>(({ onBack }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { formData, errors, updateField, validateAboutStep } = useProjectForm()
  const { areas, loading: areasLoading } = useAreas()
  const { grades, loading: gradesLoading } = useArcGrades()
  const [showModal, setShowModal] = useState(false)

  const handleTopicToggle = useCallback((topic: string) => {
    const newTopics = formData.topics.includes(topic)
      ? formData.topics.filter(t => t !== topic)
      : [...formData.topics, topic]
    updateField('topics', newTopics)
  }, [formData.topics, updateField])

  const handleCreate = useCallback(() => {
    if (validateAboutStep()) {
      setShowModal(true)
    }
  }, [validateAboutStep])

  const handleComplete = useCallback((projectId: string) => {
    navigate(`/projects/${projectId}`)
  }, [navigate])

  const handleCloseModal = useCallback(() => setShowModal(false), [])

  return (
    <Stack spacing="lg">
      <Stack spacing={0} align="flex-start">
        <Anchor onClick={onBack} sx={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <IconChevronLeft size={16} />
          {t('arc_button_back')}
        </Anchor>
        <Title order={1}>{t('new_project_title')}</Title>
        <Title order={2} weight={500} mt="xs">{t('arc_ai_flow_subtitle')}</Title>
        <Text color="dimmed" size="md">{t('arc_ai_flow_description')}</Text>
      </Stack>

      {/* Card polish only (site radius/shadow) — form fields untouched */}
      <Paper p="lg" withBorder sx={{ borderRadius: ARC_CARD_RADIUS_PX, boxShadow: ARC_CARD_SHADOW }}>
        <Stack spacing="md">
          <Group spacing={4}>
            <Text size="sm" color="red">*</Text>
            <Text fs="italic" size="lg" color="dimmed">
              {t('arc_required_fields')}
            </Text>
          </Group>

          <ProjectDescriptionSection
            value={formData.description}
            onChange={(value) => updateField('description', value)}
            error={!!errors.description}
          />

          <TopicSelectionSection
            topics={areas}
            selectedTopics={formData.topics}
            onToggle={handleTopicToggle}
            loading={areasLoading}
            error={!!errors.topics}
          />

          <GradeLevelSection
            gradeLevels={grades}
            value={formData.gradeLevel}
            onChange={(value) => updateField('gradeLevel', value)}
            loading={gradesLoading}
            error={!!errors.gradeLevel}
          />

          <ProjectPlanningSection
            planning={formData.planning}
            onChange={(field, value) => updateField(`planning.${String(field)}`, value)}
          />
        </Stack>

        <Group position="apart" mt="xl">
          <Button
            variant="default"
            leftIcon={<IconChevronLeft size={18} />}
            size="lg"
            onClick={onBack}
          >
            {t('arc_button_back')}
          </Button>
          <Button
            leftIcon={<IconSparkles size={18} />}
            size="lg"
            onClick={handleCreate}
            sx={{
              color: '#fff',
              border: 0,
              backgroundImage: ARC_AI_GRADIENT,
              '&:not(:disabled):hover': { backgroundImage: ARC_AI_GRADIENT_HOVER },
              // Let Mantine's disabled gray win over the gradient
              '&:disabled': { backgroundImage: 'none' },
            }}
          >
            {t('arc_create_project_with_ai')}
          </Button>
        </Group>
      </Paper>

      <AILoadingModal
        opened={showModal}
        onClose={handleCloseModal}
        onComplete={handleComplete}
        formData={formData}
        areas={areas}
      />
    </Stack>
  )
})

AIFlowView.displayName = 'AIFlowView'
